// src/components/Chat/EntityChat.js
import React, { useEffect, useState, useRef } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Slide,
  Popover,
  Badge,
  Tooltip,
  Paper,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import ReplyIcon from "@mui/icons-material/Reply";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";
import { socket } from "../../socket";
import {
  getEntityChatMensajes,
  postEntityChatMensaje,
  deleteEntityChatMensaje,
  getEntityChatParticipantes,
  postEntityChatParticipante,
  deleteEntityChatParticipante,
  marcarEntityChatLeidos,
} from "../../api/Chat/entityChatApi";
import { getResponsablesTodos } from "../../api/Proyectos/ProyectosTareasApi";
import { setBrowserTabBadge } from "../../utils/browserTabBadge";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const participantAbbr = (p) => {
  if (p?.abreviatura) return String(p.abreviatura).toUpperCase();
  if (!p?.username) return "?";
  const parts = String(p.username).split(" ").filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0][0].toUpperCase();
};

const fileNameFromRef = (fileRef = "") =>
  String(fileRef).replace(/\\/g, "/").split("/").pop() || fileRef;

const snippetText = (mensaje, max = 80) => {
  if (!mensaje) return "";
  if (String(mensaje).startsWith("📎 File uploaded:")) {
    return `📎 ${fileNameFromRef(mensaje.replace("📎 File uploaded:", "").trim())}`;
  }
  const text = String(mensaje).replace(/\s+/g, " ").trim();
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

const extractMentionIds = (text, usuarios) => {
  if (!text || !usuarios?.length) return [];
  const sorted = [...usuarios].sort(
    (a, b) => String(b.username || "").length - String(a.username || "").length
  );
  const ids = new Set();
  for (const u of sorted) {
    const name = String(u.username || "").trim();
    if (!name) continue;
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`(?:^|[\\s])@${escaped}(?=$|[\\s.,!?;:])`, "gi");
    if (re.test(text)) ids.add(Number(u.id));
  }
  return [...ids];
};

const MentionText = ({ text, usuarios }) => {
  if (!text) return null;
  const names = (usuarios || [])
    .map((u) => String(u.username || "").trim())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  if (!names.length) {
    return (
      <Typography variant="body2" sx={{ whiteSpace: "pre-line", color: "#333" }}>
        {text}
      </Typography>
    );
  }
  const pattern = new RegExp(
    `(@(?:${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")}))`,
    "gi"
  );
  const parts = String(text).split(pattern);
  return (
    <Typography variant="body2" sx={{ whiteSpace: "pre-line", color: "#333" }}>
      {parts.map((part, i) =>
        part.startsWith("@") ? (
          <Box
            component="span"
            key={`${part}-${i}`}
            sx={{
              color: "#1565c0",
              fontWeight: 700,
              bgcolor: "rgba(21,101,192,0.08)",
              borderRadius: 0.5,
              px: 0.25,
            }}
          >
            {part}
          </Box>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </Typography>
  );
};

const downloadFromUrl = async (url, fileName) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = fileName || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "download";
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
};

/** Attachment preview: default /uploads/, or async resolveAttachment for entity FS (muestras). */
const ChatFileAttachment = ({ fileRef, resolveAttachment }) => {
  const [loading, setLoading] = useState(Boolean(resolveAttachment));
  const [url, setUrl] = useState(
    resolveAttachment
      ? null
      : `${process.env.REACT_APP_API_URL}/uploads/${fileRef}`
  );
  const [displayName, setDisplayName] = useState(fileNameFromRef(fileRef));
  const [error, setError] = useState(false);
  const isImage = /\.(png|jpe?g|gif|webp)$/i.test(displayName);
  const isPdf = /\.pdf$/i.test(displayName);

  useEffect(() => {
    if (!resolveAttachment) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(false);
        const result = await resolveAttachment(fileRef);
        if (cancelled) return;
        if (result?.url) {
          setUrl(result.url);
          if (result.fileName) setDisplayName(result.fileName);
        } else {
          setError(true);
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fileRef, resolveAttachment]);

  const handleDownload = (e) => {
    e?.stopPropagation?.();
    if (url) downloadFromUrl(url, displayName);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
        <CircularProgress size={18} />
        <Typography variant="caption" color="text.secondary">
          {displayName}
        </Typography>
      </Box>
    );
  }

  if (error || !url) {
    return (
      <Typography variant="body2" sx={{ mt: 0.5, color: "#666" }}>
        {displayName}
      </Typography>
    );
  }

  if (isImage) {
    return (
      <Box sx={{ mt: 0.5, maxWidth: 280 }}>
        <Box
          sx={{
            position: "relative",
            display: "inline-block",
            maxWidth: "100%",
            borderRadius: 1,
            overflow: "hidden",
            border: "1px solid #ddd",
            "&:hover .chat-dl-btn": { opacity: 1 },
          }}
        >
          <Box
            component="img"
            src={url}
            alt={displayName}
            sx={{
              display: "block",
              maxWidth: "100%",
              maxHeight: 220,
              cursor: "pointer",
            }}
            onClick={() => window.open(url, "_blank")}
          />
          <Tooltip title="Download">
            <IconButton
              className="chat-dl-btn"
              size="small"
              onClick={handleDownload}
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                bgcolor: "rgba(0,0,0,0.55)",
                color: "#fff",
                opacity: { xs: 1, sm: 0.85 },
                transition: "opacity 0.15s",
                "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.4,
            color: "#666",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {displayName}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mt: 0.5,
        px: 1,
        py: 0.75,
        borderRadius: 1.5,
        bgcolor: "rgba(0,0,0,0.04)",
        border: "1px solid #e0e0e0",
        maxWidth: 280,
        cursor: "pointer",
        "&:hover": { bgcolor: "rgba(0,0,0,0.07)" },
      }}
      onClick={handleDownload}
    >
      {isPdf ? (
        <Box
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: "50%",
            bgcolor: "#d32f2f",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        >
          PDF
        </Box>
      ) : (
        <InsertDriveFileOutlinedIcon sx={{ color: "#1976d2", flexShrink: 0 }} />
      )}
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          color: "#1976d2",
          fontWeight: 600,
          fontSize: "0.85rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {displayName}
      </Typography>
      <Tooltip title="Download">
        <IconButton size="small" onClick={handleDownload} sx={{ flexShrink: 0 }}>
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const EntityChat = ({
  open,
  onClose,
  entityType,
  entityId,
  title,
  subtitle,
  showDocumentsTab = false,
  uploadFile,
  documentsContent,
  resolveAttachment,
  /** Optional second tab: { label, content } — preferred over showDocumentsTab */
  secondaryTab = null,
  /** 0 | "mensajes" | 1 | "notas" | "documentos" */
  initialTab = 0,
}) => {
  const hasSecondaryTab = Boolean(
    secondaryTab?.content != null || showDocumentsTab
  );
  const secondaryLabel =
    secondaryTab?.label || (showDocumentsTab ? "Documentos" : "Notas");
  const secondaryContent =
    secondaryTab?.content ?? documentsContent ?? null;

  const resolveInitialTab = (value) => {
    if (value === 1 || value === "notas" || value === "documentos") return 1;
    return 0;
  };

  const [tab, setTab] = useState(() => resolveInitialTab(initialTab));
  const [mensajes, setMensajes] = useState([]);
  const [participantes, setParticipantes] = useState([]);
  const [nuevoMensaje, setNuevoMensaje] = useState("");
  const [usuarios, setUsuarios] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const composerBoxRef = useRef(null);
  const messageRefs = useRef({});

  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef(null);
  const canUpload = typeof uploadFile === "function";

  const decoded = JSON.parse(atob(localStorage.getItem("token").split(".")[1]));
  const userId = decoded.userId;
  const username = decoded.username;
  const email = decoded.email;
  const abreviatura = decoded.abreviatura;

  const [openDeleteParticipantDialog, setOpenDeleteParticipantDialog] =
    useState(false);
  const [participantToDelete, setParticipantToDelete] = useState(null);
  const [openDeleteMessageDialog, setOpenDeleteMessageDialog] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [participantsAnchor, setParticipantsAnchor] = useState(null);

  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStart, setMentionStart] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionAnchor, setMentionAnchor] = useState(null);

  const normalizeMsg = (m) => {
    const autorData = { ...(m.autor || m.Autor || {}) };
    if (!autorData.abreviatura && autorData.username) {
      const nameParts = autorData.username.split(" ").filter(Boolean);
      autorData.abreviatura =
        nameParts.length >= 2
          ? (nameParts[0][0] + nameParts[1][0]).toUpperCase()
          : nameParts[0][0].toUpperCase();
    }
    const respuestaA = m.respuestaA || m.RespuestaA || null;
    return { ...m, autor: autorData, respuestaA };
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const scrollToMessage = (id) => {
    const el = messageRefs.current[id];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.style.outline = "2px solid #1976d2";
    setTimeout(() => {
      el.style.outline = "";
    }, 1200);
  };

  const showSnack = (msg, severity = "success") => {
    setSnackbarMsg(msg);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    if (!entityType || !entityId || !open) return;
    const loadData = async () => {
      setLoading(true);
      try {
        const [msgs, parts, users] = await Promise.all([
          getEntityChatMensajes(entityType, entityId),
          getEntityChatParticipantes(entityType, entityId),
          getResponsablesTodos(),
        ]);
        setMensajes((msgs || []).map(normalizeMsg));
        setParticipantes(parts);
        setUsuarios(users);
      } catch (err) {
        console.error("❌ Error cargando chat:", err);
      } finally {
        setLoading(false);
        scrollToBottom();
      }
    };
    loadData();
  }, [entityType, entityId, open]);

  useEffect(() => {
    if (!open) return;
    const next = resolveInitialTab(initialTab);
    setTab(hasSecondaryTab ? next : 0);
  }, [open, initialTab, hasSecondaryTab, entityId]);

  useEffect(() => {
    if (!entityType || !entityId) return;
    const handleNuevoMensaje = ({
      entityType: et,
      entityId: eid,
      mensaje,
      mentionedUserIds,
    }) => {
      if (et !== entityType || Number(eid) !== Number(entityId)) return;
      const mentioned = (mentionedUserIds || []).map(Number).includes(Number(userId));
      if (mensaje.AutorId === userId) return;
      setMensajes((prev) => {
        if (prev.some((m) => Number(m.Id) === Number(mensaje.Id))) return prev;
        return [...prev, normalizeMsg(mensaje)];
      });
      scrollToBottom();
      if (mentioned && open) {
        showSnack(
          `${mensaje.autor?.username || "Someone"} mentioned you`,
          "info"
        );
      }
    };
    const handleMensajeEliminado = ({
      entityType: et,
      entityId: eid,
      mensaje,
    }) => {
      if (et !== entityType || Number(eid) !== Number(entityId)) return;
      setMensajes((prev) =>
        prev.map((m) =>
          Number(m.Id) === Number(mensaje.Id) ? normalizeMsg(mensaje) : m
        )
      );
    };
    socket.on("chat-nuevo-mensaje", handleNuevoMensaje);
    socket.on("chat-mensaje-eliminado", handleMensajeEliminado);
    return () => {
      socket.off("chat-nuevo-mensaje", handleNuevoMensaje);
      socket.off("chat-mensaje-eliminado", handleMensajeEliminado);
    };
  }, [entityType, entityId, userId, open]);

  useEffect(() => {
    const marcarLeidos = async () => {
      if (open && entityType && entityId) {
        try {
          await marcarEntityChatLeidos(entityType, entityId);
          setBrowserTabBadge(false);
        } catch (err) {
          console.error("❌ Error marcando mensajes leídos:", err);
        }
      }
    };
    marcarLeidos();
  }, [open, entityType, entityId]);

  const mentionCandidates = (usuarios || [])
    .filter((u) => Number(u.id) !== Number(userId))
    .filter((u) => {
      if (!mentionQuery) return true;
      const q = mentionQuery.toLowerCase();
      return (
        String(u.username || "").toLowerCase().includes(q) ||
        String(u.abreviatura || "").toLowerCase().includes(q)
      );
    })
    .slice(0, 8);

  const updateMentionState = (value, caret) => {
    const before = value.slice(0, caret);
    const match = before.match(/(^|[\s])@([^\s@]*)$/);
    if (!match) {
      setMentionOpen(false);
      setMentionQuery("");
      setMentionStart(null);
      return;
    }
    setMentionStart(caret - match[2].length - 1);
    setMentionQuery(match[2] || "");
    setMentionIndex(0);
    setMentionOpen(true);
    setMentionAnchor(composerBoxRef.current);
  };

  const insertMention = (user) => {
    if (mentionStart == null || !user?.username) return;
    const input = messageInputRef.current;
    const caret = input?.selectionStart ?? nuevoMensaje.length;
    const before = nuevoMensaje.slice(0, mentionStart);
    const after = nuevoMensaje.slice(caret);
    const insertion = `@${user.username} `;
    const next = `${before}${insertion}${after}`;
    setNuevoMensaje(next);
    setMentionOpen(false);
    setMentionQuery("");
    setMentionStart(null);
    setTimeout(() => {
      const el = messageInputRef.current;
      if (!el) return;
      const pos = before.length + insertion.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleSend = async () => {
    if (!nuevoMensaje.trim() || !entityType || !entityId) return;
    setIsSending(true);
    try {
      const text = nuevoMensaje.trim();
      const menciones = extractMentionIds(text, usuarios);
      const payload = {
        Mensaje: text,
        Menciones: menciones,
      };
      if (replyTo?.Id) payload.ReplyToMensajeId = replyTo.Id;

      const msg = await postEntityChatMensaje(entityType, entityId, payload);
      const normalized = normalizeMsg({
        ...msg,
        autor: msg.autor || { id: userId, username, email, abreviatura },
      });
      setMensajes((prev) => {
        if (prev.some((m) => Number(m.Id) === Number(normalized.Id))) {
          return prev;
        }
        return [...prev, normalized];
      });
      setNuevoMensaje("");
      setReplyTo(null);
      setMentionOpen(false);
      scrollToBottom();

      if (menciones.length) {
        const updated = await getEntityChatParticipantes(entityType, entityId);
        setParticipantes(updated);
      }
    } catch (err) {
      console.error("❌ Error enviando mensaje:", err);
      showSnack("Error sending message", "error");
    } finally {
      setIsSending(false);
    }
  };

  const uploadFiles = async (files) => {
    if (!canUpload || !files.length || !entityType || !entityId) return;
    try {
      setIsSending(true);
      for (const file of files) {
        const mensajeText = await uploadFile(file);
        if (!mensajeText) continue;
        const payload = { Mensaje: mensajeText };
        if (replyTo?.Id) payload.ReplyToMensajeId = replyTo.Id;
        const msg = await postEntityChatMensaje(entityType, entityId, payload);
        const normalized = normalizeMsg({
          ...msg,
          autor: msg.autor || { id: userId, username, abreviatura, email },
        });
        setMensajes((prev) => [...prev, normalized]);
      }
      setReplyTo(null);
      scrollToBottom();
    } catch (err) {
      console.error("❌ Error subiendo archivo:", err);
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (!open || !canUpload) return;

    const handleDragOver = (e) => {
      e.preventDefault();
      setIsDragging(true);
    };
    const handleDragLeave = (e) => {
      e.preventDefault();
      if (e.target === document || e.target === window) setIsDragging(false);
    };
    const handleDrop = (e) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer?.files || []);
      uploadFiles(files);
    };
    const handlePaste = (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      const files = items
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile());
      if (files.length) uploadFiles(files);
    };

    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);
    window.addEventListener("paste", handlePaste);

    return () => {
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
      window.removeEventListener("paste", handlePaste);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canUpload, entityType, entityId, replyTo]);

  const colorByUser = (name) => {
    const palette = ["#42A5F5", "#66BB6A", "#FF7043", "#AB47BC", "#26A69A"];
    const index = (name || "?").charCodeAt(0) % palette.length;
    return palette[index];
  };

  const handleAddParticipant = async (uid) => {
    if (!uid || !entityType || !entityId) return;
    try {
      await postEntityChatParticipante(entityType, entityId, {
        UserId: parseInt(uid),
      });
      const updated = await getEntityChatParticipantes(entityType, entityId);
      setParticipantes(updated);
      showSnack("Participant added successfully");
    } catch (err) {
      console.error("❌ Error añadiendo participante:", err);
      showSnack("Error adding participant", "error");
    }
  };

  const handleRemoveParticipant = async () => {
    if (!participantToDelete || !entityType || !entityId) return;
    try {
      await deleteEntityChatParticipante(
        entityType,
        entityId,
        participantToDelete.id
      );
      setParticipantes((prev) =>
        prev.filter((p) => p.id !== participantToDelete.id)
      );
      showSnack("Participant removed successfully");
      setOpenDeleteParticipantDialog(false);
    } catch (err) {
      console.error("❌ Error removing participant:", err);
      showSnack("Error removing participant", "error");
    }
  };

  const handleConfirmDeleteMessage = async () => {
    if (!messageToDelete || !entityType || !entityId) return;
    try {
      const updated = await deleteEntityChatMensaje(
        entityType,
        entityId,
        messageToDelete.Id
      );
      setMensajes((prev) =>
        prev.map((m) =>
          Number(m.Id) === Number(updated.Id) ? normalizeMsg(updated) : m
        )
      );
      if (replyTo && Number(replyTo.Id) === Number(messageToDelete.Id)) {
        setReplyTo(null);
      }
      showSnack("Message deleted");
      setOpenDeleteMessageDialog(false);
      setMessageToDelete(null);
    } catch (err) {
      console.error("❌ Error deleting message:", err);
      showSnack("Error deleting message", "error");
    }
  };

  const handleComposerKeyDown = (e) => {
    if (mentionOpen && mentionCandidates.length) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIndex((i) => (i + 1) % mentionCandidates.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIndex(
          (i) => (i - 1 + mentionCandidates.length) % mentionCandidates.length
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(mentionCandidates[mentionIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionOpen(false);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!entityType || !entityId) return null;

  const showMessages = !hasSecondaryTab || tab === 0;
  const showSecondary = hasSecondaryTab && tab === 1;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: "100vw", md: 620 },
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          p: 2,
          pb: 1.25,
          borderBottom: "1px solid #ddd",
        }}
      >
        <Box sx={{ minWidth: 0, pr: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
            {showSecondary
              ? `${secondaryLabel}: ${title}`
              : `Chat: ${title}`}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" sx={{ color: "#666" }} noWrap>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          gap: 1,
          bgcolor: "#fafafa",
        }}
      >
        <Tooltip title="Participants">
          <IconButton
            size="small"
            onClick={(e) => setParticipantsAnchor(e.currentTarget)}
            sx={{ p: 0.5 }}
          >
            <Badge
              badgeContent={participantes.length}
              color="primary"
              max={99}
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.65rem",
                  height: 16,
                  minWidth: 16,
                },
              }}
            >
              <GroupOutlinedIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
            cursor: "pointer",
          }}
          onClick={(e) => setParticipantsAnchor(e.currentTarget)}
        >
          {participantes.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No participants yet — click to add
            </Typography>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {participantes.slice(0, 5).map((p, idx) => (
                <Tooltip key={p.id} title={p.username || ""}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      bgcolor: p.colorHex || colorByUser(p.username || "?"),
                      color: "#fff",
                      border: "2px solid #fff",
                      ml: idx === 0 ? 0 : -0.75,
                      zIndex: 5 - idx,
                    }}
                  >
                    {participantAbbr(p)}
                  </Avatar>
                </Tooltip>
              ))}
              {participantes.length > 5 && (
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    bgcolor: "#90a4ae",
                    color: "#fff",
                    border: "2px solid #fff",
                    ml: -0.75,
                  }}
                >
                  +{participantes.length - 5}
                </Avatar>
              )}
            </Box>
          )}
        </Box>

        <Tooltip title="Add participant">
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => setParticipantsAnchor(e.currentTarget)}
          >
            <PersonAddAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {hasSecondaryTab && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              ml: 0.5,
              pl: 0.75,
              borderLeft: "1px solid #e0e0e0",
            }}
          >
            <Tooltip title="Mensajes">
              <IconButton
                size="small"
                onClick={() => setTab(0)}
                sx={{
                  color: tab === 0 ? "primary.main" : "text.secondary",
                  bgcolor: tab === 0 ? "rgba(25,118,210,0.1)" : "transparent",
                  borderRadius: 1.5,
                  "&:hover": {
                    bgcolor:
                      tab === 0
                        ? "rgba(25,118,210,0.16)"
                        : "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <ChatBubbleOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={secondaryLabel}>
              <IconButton
                size="small"
                onClick={() => setTab(1)}
                sx={{
                  color: tab === 1 ? "warning.dark" : "text.secondary",
                  bgcolor:
                    tab === 1 ? "rgba(237,108,2,0.12)" : "transparent",
                  borderRadius: 1.5,
                  "&:hover": {
                    bgcolor:
                      tab === 1
                        ? "rgba(237,108,2,0.18)"
                        : "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <StickyNote2OutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      <Popover
        open={Boolean(participantsAnchor)}
        anchorEl={participantsAnchor}
        onClose={() => setParticipantsAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: { width: 340, maxWidth: "90vw", p: 1.5, maxHeight: 420 },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Participants ({participantes.length})
        </Typography>

        {participantes.length === 0 ? (
          <Typography sx={{ color: "#888", fontSize: "0.85rem", mb: 1.5 }}>
            No participants yet.
          </Typography>
        ) : (
          <List dense sx={{ mb: 1, maxHeight: 240, overflowY: "auto" }}>
            {participantes.map((p) => (
              <ListItem
                key={p.id}
                sx={{
                  border: "1px solid #eee",
                  borderRadius: 1.5,
                  mb: 0.75,
                  px: 1,
                  py: 0.25,
                  bgcolor: "#fafafa",
                }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    sx={{ color: "#d32f2f" }}
                    onClick={() => {
                      setParticipantToDelete(p);
                      setOpenDeleteParticipantDialog(true);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor: p.colorHex || colorByUser(p.username || "?"),
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      width: 32,
                      height: 32,
                    }}
                  >
                    {participantAbbr(p)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={p.username}
                  secondary={p.email}
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: "0.85rem",
                  }}
                  secondaryTypographyProps={{
                    fontSize: "0.75rem",
                    color: "#666",
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Divider sx={{ my: 1 }} />

        <TextField
          select
          fullWidth
          size="small"
          label="Add participant"
          SelectProps={{ native: true }}
          onChange={(e) => {
            if (!e.target.value) return;
            handleAddParticipant(e.target.value);
            e.target.value = "";
          }}
        >
          <option value=""></option>
          {usuarios
            .filter(
              (u) => !participantes.some((p) => Number(p.id) === Number(u.id))
            )
            .map((u) => (
              <option key={u.id} value={u.id}>
                {u.username}
              </option>
            ))}
        </TextField>
      </Popover>

      {showMessages && (
        <Box
          ref={dropZoneRef}
          sx={{
            flex: 1,
            p: 2,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          {isDragging && canUpload && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(0,0,0,0.6)",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "3px dashed #00C2A3",
                borderRadius: 2,
                color: "#fff",
                fontSize: "1.3rem",
                fontWeight: 600,
                backdropFilter: "blur(6px)",
              }}
            >
              📎 Drop or paste files to upload
            </Box>
          )}

          {loading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : mensajes.length === 0 ? (
            <Typography sx={{ color: "#888", mt: 2 }}>
              There are no messages yet. Start the conversation!
            </Typography>
          ) : (
            mensajes.map((m) => {
              const isMine = m.AutorId === userId;
              const userColor =
                m.autor?.colorHex || colorByUser(m.autor?.username || "?");
              const isDeleted = Boolean(m.Eliminado);
              const quoted = m.respuestaA;
              return (
                <Box
                  key={m.Id}
                  ref={(el) => {
                    if (el) messageRefs.current[m.Id] = el;
                  }}
                  sx={{
                    mb: 1.5,
                    display: "flex",
                    flexDirection: isMine ? "row-reverse" : "row",
                  }}
                >
                  <Avatar
                    sx={{
                      ml: isMine ? 1 : 0,
                      mr: isMine ? 0 : 1,
                      color: "#ffffffff",
                      fontWeight: 600,
                      border: isMine
                        ? "1px solid #2196F3"
                        : "1px solid transparent",
                      bgcolor: userColor,
                    }}
                  >
                    {m.autor?.abreviatura ||
                      m.autor?.username?.[0]?.toUpperCase() ||
                      "?"}
                  </Avatar>
                  <Box
                    sx={{
                      p: 1.2,
                      borderRadius: 2,
                      backgroundColor: isMine ? "#E3F2FD" : "#F9F9F9",
                      border: `1px solid ${isMine ? "#90CAF9" : "#E0E0E0"}`,
                      maxWidth: "95%",
                      width: "65%",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      position: "relative",
                      "&:hover .msg-actions": { opacity: 1 },
                    }}
                  >
                    <Box
                      className="msg-actions"
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: isMine ? "auto" : 4,
                        left: isMine ? 4 : "auto",
                        display: "flex",
                        gap: 0.25,
                        opacity: { xs: 1, sm: 0 },
                        transition: "opacity 0.15s",
                        bgcolor: "rgba(255,255,255,0.9)",
                        borderRadius: 1,
                      }}
                    >
                      {!isDeleted && (
                        <Tooltip title="Reply">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setReplyTo(m);
                              messageInputRef.current?.focus();
                            }}
                          >
                            <ReplyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isMine && !isDeleted && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setMessageToDelete(m);
                              setOpenDeleteMessageDialog(true);
                            }}
                          >
                            <DeleteOutlineIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: userColor,
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        mb: 0.3,
                        pr: 4,
                      }}
                    >
                      {m.autor?.username || "Usuario"}
                    </Typography>

                    {quoted && (
                      <Box
                        onClick={() => scrollToMessage(quoted.Id)}
                        sx={{
                          mb: 0.75,
                          px: 1,
                          py: 0.5,
                          borderLeft: "3px solid #1976d2",
                          bgcolor: "rgba(25,118,210,0.06)",
                          borderRadius: "0 6px 6px 0",
                          cursor: "pointer",
                          "&:hover": { bgcolor: "rgba(25,118,210,0.12)" },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, color: "#1976d2", display: "block" }}
                        >
                          {quoted.autor?.username || "Usuario"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#555",
                            display: "block",
                            fontStyle: quoted.Eliminado ? "italic" : "normal",
                          }}
                        >
                          {quoted.Eliminado
                            ? "Mensaje eliminado"
                            : snippetText(quoted.Mensaje)}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ mt: 0.5 }}>
                      {isDeleted ? (
                        <Typography
                          variant="body2"
                          sx={{
                            fontStyle: "italic",
                            color: "#888",
                          }}
                        >
                          Mensaje eliminado
                        </Typography>
                      ) : m.Mensaje?.startsWith("📎 File uploaded:") ? (
                        <ChatFileAttachment
                          fileRef={m.Mensaje.replace(
                            "📎 File uploaded: ",
                            ""
                          ).trim()}
                          resolveAttachment={resolveAttachment}
                        />
                      ) : (
                        <MentionText text={m.Mensaje} usuarios={usuarios} />
                      )}
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        color: "#777",
                        mt: 0.4,
                        display: "block",
                        textAlign: isMine ? "right" : "left",
                      }}
                    >
                      {dayjs(m.Fecha).format("DD/MM HH:mm")}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </Box>
      )}

      {showSecondary && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {secondaryContent || (
            <Box sx={{ p: 2 }}>
              <Typography sx={{ color: "#888" }}>No content.</Typography>
            </Box>
          )}
        </Box>
      )}

      {showMessages && (
        <Box
          sx={{
            p: 1.5,
            borderTop: "1px solid #ddd",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {replyTo && (
            <Paper
              variant="outlined"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1,
                py: 0.75,
                bgcolor: "#f5f9ff",
                borderColor: "#90CAF9",
              }}
            >
              <ReplyIcon sx={{ color: "#1976d2", fontSize: 18 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: "#1976d2", display: "block" }}
                >
                  Replying to {replyTo.autor?.username || "Usuario"}
                </Typography>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ color: "#555", display: "block" }}
                >
                  {replyTo.Eliminado
                    ? "Mensaje eliminado"
                    : snippetText(replyTo.Mensaje)}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setReplyTo(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          )}

          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
            {canUpload && (
              <IconButton color="primary" component="label" disabled={isSending}>
                <AttachFileIcon />
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={(e) =>
                    uploadFiles(Array.from(e.target.files || []))
                  }
                />
              </IconButton>
            )}
            <Box sx={{ flex: 1, position: "relative" }} ref={composerBoxRef}>
              <TextField
                fullWidth
                multiline
                maxRows={6}
                minRows={1}
                placeholder="Escribe tu mensaje... (@ para mencionar)"
                size="small"
                value={nuevoMensaje}
                inputRef={messageInputRef}
                onChange={(e) => {
                  const value = e.target.value;
                  setNuevoMensaje(value);
                  updateMentionState(value, e.target.selectionStart ?? value.length);
                }}
                onKeyDown={handleComposerKeyDown}
                onClick={(e) =>
                  updateMentionState(
                    e.target.value,
                    e.target.selectionStart ?? e.target.value.length
                  )
                }
                sx={{
                  "& .MuiInputBase-root": {
                    alignItems: "flex-start",
                  },
                }}
              />
              <Popover
                open={mentionOpen && mentionCandidates.length > 0}
                anchorEl={mentionAnchor}
                onClose={() => setMentionOpen(false)}
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                PaperProps={{
                  sx: { width: 280, maxHeight: 240, mb: 0.5 },
                }}
              >
                <List dense disablePadding>
                  {mentionCandidates.map((u, idx) => (
                    <ListItemButton
                      key={u.id}
                      selected={idx === mentionIndex}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        insertMention(u);
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: "0.7rem",
                            bgcolor: u.colorHex || colorByUser(u.username || "?"),
                          }}
                        >
                          {participantAbbr(u)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={u.username}
                        secondary={u.email}
                        primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600 }}
                        secondaryTypographyProps={{ fontSize: "0.7rem" }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Popover>
            </Box>
            <IconButton
              color="primary"
              disabled={isSending || !nuevoMensaje.trim()}
              onClick={handleSend}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      <Dialog
        open={openDeleteParticipantDialog}
        TransitionComponent={Transition}
        onClose={() => setOpenDeleteParticipantDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#d32f2f",
          }}
        >
          <WarningAmberIcon sx={{ color: "#d32f2f" }} /> Remove participant
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this participant from the chat?
          </DialogContentText>
          {participantToDelete && (
            <Box
              sx={{
                backgroundColor: "#f9f9f9",
                borderRadius: 1,
                p: 1,
                border: "1px solid #ddd",
              }}
            >
              {participantToDelete.username} ({participantToDelete.email})
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteParticipantDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRemoveParticipant}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteMessageDialog}
        TransitionComponent={Transition}
        onClose={() => setOpenDeleteMessageDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#d32f2f",
          }}
        >
          <WarningAmberIcon sx={{ color: "#d32f2f" }} /> Delete message
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this message? It will be replaced
            with &quot;Mensaje eliminado&quot;.
          </DialogContentText>
          {messageToDelete && (
            <Box
              sx={{
                backgroundColor: "#f9f9f9",
                borderRadius: 1,
                p: 1,
                mt: 1,
                border: "1px solid #ddd",
                maxHeight: 100,
                overflow: "auto",
              }}
            >
              {snippetText(messageToDelete.Mensaje, 160)}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDeleteMessageDialog(false);
              setMessageToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteMessage}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Drawer>
  );
};

export default EntityChat;
